import React from 'react'
import { cn } from '@/lib/utils'

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Direction of the stack layout
   * @default 'column'
   */
  direction?: 'row' | 'column'
  /**
   * Spacing between child elements (1-8 maps to Tailwind gap scale)
   * @default 4
   */
  spacing?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  /**
   * Alignment of items along the cross axis (align-items)
   * @default undefined
   */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
  /**
   * Alignment of items along the main axis (justify-content)
   * @default undefined
   */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'
  /**
   * Whether to wrap items
   * @default false
   */
  wrap?: boolean
  /**
   * Custom className for additional styling
   */
  className?: string
  /**
   * Child elements
   */
  children?: React.ReactNode
}

/**
 * Stack component for flexible box layouts with spacing
 *
 * Replacement for MUI Stack with Tailwind CSS styling
 *
 * @example
 * ```tsx
 * <Stack spacing={4}>
 *   <Text variant="h2">Title</Text>
 *   <Text variant="body1">Description</Text>
 * </Stack>
 * ```
 *
 * @example
 * ```tsx
 * <Stack direction="row" spacing={2} align="center" justify="between">
 *   <Button>Cancel</Button>
 *   <Button variant="primary">Save</Button>
 * </Stack>
 * ```
 */
export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'column',
      spacing = 4,
      align,
      justify,
      wrap = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const directionClasses = {
      row: 'flex-row',
      column: 'flex-col'
    }

    const spacingClasses = {
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      5: 'gap-5',
      6: 'gap-6',
      7: 'gap-7',
      8: 'gap-8'
    }

    const alignClasses = align
      ? {
          start: 'items-start',
          center: 'items-center',
          end: 'items-end',
          stretch: 'items-stretch',
          baseline: 'items-baseline'
        }[align]
      : undefined

    const justifyClasses = justify
      ? {
          start: 'justify-start',
          center: 'justify-center',
          end: 'justify-end',
          between: 'justify-between',
          around: 'justify-around',
          evenly: 'justify-evenly'
        }[justify]
      : undefined

    return (
      <div
        ref={ref}
        className={cn(
          'flex',
          directionClasses[direction],
          spacingClasses[spacing],
          alignClasses,
          justifyClasses,
          wrap && 'flex-wrap',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Stack.displayName = 'Stack'
